/**
 * Redux Store для ProfitOption
 * Управление состоянием торгового приложения
 */
import { configureStore } from "@reduxjs/toolkit";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import authReducer, { logout } from "./slices/authSlice";
import tradingReducer from "./slices/tradingSlice";
import websocketReducer from "./slices/websocketSlice";
import { drawingReducer } from "features/drawing-tool/model";
import { indicatorsReducer } from "features/indicators/model";

import { api } from "shared/config/api/api";
import { clearStoredPair } from "features/select-pair/model/pairSessionStorage";
import { clearStoredTime } from "widgets/traiding-panel/model/timeSessionStorage";

const logoutListenerMiddleware = createListenerMiddleware();

// Inline-тип вместо RootState: store ещё не объявлен, иначе циклический импорт + TS2571.
type AuthSliceForLogout = { auth: { user: { id: number } | null } };

logoutListenerMiddleware.startListening({
  actionCreator: logout,
  effect: (_action, listenerApi) => {
    // getOriginalState — до reducer logout, user.id ещё доступен.
    const userId = (
      listenerApi.getOriginalState() as AuthSliceForLogout
    ).auth.user?.id;
    if (userId != null) {
      clearStoredPair(userId);
      clearStoredTime(userId);
    }
    listenerApi.dispatch(api.util.resetApiState());
  },
});

export const store = configureStore({
  reducer: {
    auth: authReducer,
    trading: tradingReducer,
    websocket: websocketReducer,
    drawing: drawingReducer,
    indicators: indicatorsReducer,
    [api.reducerPath]: api.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["websocket/setConnection"],
        ignoredPaths: ["websocket.connection"],
      },
    })
      .prepend(logoutListenerMiddleware.middleware)
      .concat(api.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
